import { cn } from '@/lib/utils';

/**
 * Content interface for EmptyStateCard component.
 * Follows content-props pattern for complete UI/content separation.
 */
export interface EmptyStateCardContent {
  /** Card headline text */
  headline: string;
  /** Card body text with search instructions */
  bodyText: string;
  /** Gray help text with example search terms */
  helpText: string;
}

/**
 * Props interface for EmptyStateCard component.
 */
export interface EmptyStateCardProps {
  /** Content for the empty state card. If undefined, renders nothing. */
  content?: EmptyStateCardContent;
  /** Additional CSS classes */
  className?: string;
}

/**
 * EmptyStateCard - Displays a prompt when no search query is present.
 *
 * Shows a headline, body text describing how to search, and helper text
 * with suggested search terms. Displayed on initial page load or after
 * clearing the search query.
 *
 * @example
 * ```tsx
 * import { EmptyStateCard } from '@/components';
 *
 * <EmptyStateCard
 *   content={{
 *     headline: "Search informational resources",
 *     bodyText: "Enter a keyword or phrase to quickly find what you're looking for across all themes.",
 *     helpText: "Try 'support for caregivers,' 'financial help,' 'devices'",
 *   }}
 * />
 * ```
 */
export function EmptyStateCard({ content, className }: EmptyStateCardProps) {
  if (!content) {
    return null;
  }

  return (
    <div
      className={cn(
        'bg-white border border-[#EBEBEB] rounded-[4px] shadow-[0px_4px_4px_0px_rgba(187,187,187,0.25)]',
        'p-4 sm:p-6 lg:p-10',
        className
      )}
    >
      {/* Headline */}
      <h2
        className="font-semibold text-black font-['Source_Sans_Pro']"
        style={{
          fontSize: '24px',
          lineHeight: '32px',
        }}
      >
        {content.headline}
      </h2>

      {/* Body Text */}
      <p
        className="font-normal text-black font-['Source_Sans_Pro'] mt-4"
        style={{
          fontSize: '20px',
          lineHeight: '28px',
        }}
      >
        {content.bodyText}
      </p>

      {/* Help Text */}
      <p
        className="font-normal text-[#7A7A7A] font-['Source_Sans_Pro'] mt-4"
        style={{
          fontSize: '16px',
          lineHeight: '24px',
        }}
      >
        {content.helpText}
      </p>
    </div>
  );
}

EmptyStateCard.displayName = 'EmptyStateCard';
